/**
 * Conversation Notes Hooks
 *
 * Provides hooks for conversation notes CRUD operations using raClient.
 * Notes are now returned in snake_case format directly from the backend.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { raClient, type RAListParams } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'
import type { Note, NoteStatus, PriorityLevel } from '@/types/conversation'
import { api } from '@/lib/api'

// ============================================================================
// Query Keys
// ============================================================================

export const noteKeys = createQueryKeys('conversation-notes')

// ============================================================================
// Types
// ============================================================================

interface CreateNoteData {
  conversation_id: string | number
  content: string
  status?: NoteStatus
  priority?: PriorityLevel | null
  is_private?: boolean
  assigned_to?: number | null
  assigned_team_id?: number | null
  mentioned_user_ids?: number[]
  mentioned_team_ids?: number[]
}

interface CreateReplyData {
  noteId: string
  content: string
  is_private?: boolean
  close_note?: boolean
  mentioned_user_ids?: number[]
  mentioned_team_ids?: number[]
}

interface UpdateNoteData {
  id: string
  content?: string
  status?: NoteStatus
  priority?: PriorityLevel | null
  assigned_to?: number | null
  assigned_team_id?: number | null
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch notes for a conversation
 */
export function useConversationNotes(conversationId: string | number | undefined) {
  const params: RAListParams = {
    page: 1,
    perPage: 100,
    sort: { field: 'created_at', order: 'DESC' },
    filter: { conversation_id: conversationId },
  }

  return useQuery({
    queryKey: [...noteKeys.list(params), conversationId],
    queryFn: async (): Promise<Note[]> => {
      const response = await raClient.getList<Note>('conversation-notes', params)
      return response.data
    },
    enabled: !!conversationId,
  })
}

/**
 * Hook to create a note
 */
export function useCreateNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateNoteData) =>
      raClient.create<Note>('conversation-notes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.all })
    },
  })
}

/**
 * Hook to update a note
 */
export function useUpdateNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateNoteData) =>
      raClient.update<Note>('conversation-notes', id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.all })
    },
  })
}

/**
 * Hook to delete a note
 */
export function useDeleteNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => raClient.delete('conversation-notes', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.all })
    },
  })
}

/**
 * Hook to create a reply on a note
 * Note: Replies use a custom endpoint, not the standard RA format
 */
export function useCreateReply() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ noteId, ...data }: CreateReplyData) => {
      const response = await api.post(`/conversation-notes/${noteId}/replies`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.all })
    },
  })
}

/**
 * Hook to update a reply
 * Note: Replies use a custom endpoint, not the standard RA format
 */
export function useUpdateReply() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      replyId,
      content,
      is_private,
    }: {
      replyId: string
      content: string
      is_private?: boolean
    }) => {
      const response = await api.put(`/conversation-notes/replies/${replyId}`, {
        content,
        is_private,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.all })
    },
  })
}
